import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { TrendingUp, DollarSign, Percent, Target } from 'lucide-react';
import { formatCurrency } from '../../lib/formatters';

const KPICard = ({ title, value, icon: Icon, color = 'blue', subtitle }) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium text-gray-600">{title}</CardTitle>
      <Icon className={`h-4 w-4 text-${color}-500`} />
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
      {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
    </CardContent>
  </Card>
);

export const GlobalKPICards = ({ summary }) => {
  if (!summary || summary.length === 0) return null;

  const totalInvested = summary.reduce((sum, s) => sum + (s.total_advance || 0), 0);
  const totalCollected = summary.reduce((sum, s) => sum + (s.total_collected || 0), 0);
  const totalBalance = summary.reduce((sum, s) => sum + (s.total_balance || 0), 0);
  const averageROI = totalInvested > 0 
    ? ((totalCollected - totalInvested) / totalInvested * 100).toFixed(2) 
    : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <KPICard
        title="Total Invested"
        value={formatCurrency(totalInvested)}
        icon={DollarSign}
        color="blue"
      />
      <KPICard
        title="Total Collected"
        value={formatCurrency(totalCollected)}
        icon={TrendingUp}
        color="green"
      />
      <KPICard
        title="Available Balance"
        value={formatCurrency(totalBalance)}
        icon={Target}
        color="purple"
      />
      <KPICard
        title="Average ROI"
        value={`${averageROI}%`}
        icon={Percent}
        color={parseFloat(averageROI) >= 0 ? 'green' : 'red'}
      />
    </div>
  );
};

export const SyndicatorKPICards = ({ syndicator }) => {
  if (!syndicator) return null;

  const roi = syndicator.total_advance > 0
    ? ((syndicator.total_collected - syndicator.total_advance) / syndicator.total_advance * 100).toFixed(2)
    : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
      <KPICard
        title="Total Invested"
        value={formatCurrency(syndicator.total_advance || 0)}
        icon={DollarSign}
        color="blue"
      />
      <KPICard
        title="Expected Return"
        value={formatCurrency(syndicator.total_target || 0)}
        icon={Target}
        color="purple"
      />
      <KPICard
        title="Collected So Far"
        value={formatCurrency(syndicator.total_collected || 0)}
        icon={TrendingUp}
        color="green"
      />
      <KPICard
        title="Available Balance"
        value={formatCurrency(syndicator.total_balance || 0)}
        icon={DollarSign}
        color="indigo"
      />
      <KPICard
        title="Current ROI"
        value={`${roi}%`}
        icon={Percent}
        color={parseFloat(roi) >= 0 ? 'green' : 'red'}
      />
    </div>
  );
};

export default { GlobalKPICards, SyndicatorKPICards };
